import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import {
  isNoResponse,
  readStatusCode,
  retryOnServerError,
} from '@/utils/apiErrors';
import { Logger } from '@/utils/logging';

import type { ShopDetailResponse, UseShopDetailQueryOptions } from './types';

async function getShopDetail(shopId: string, include: string[] = []) {
  try {
    const response = await apiClient.get<ShopDetailResponse>(
      `/v1/shop/${shopId}`,
      {
        params: {
          include: include.join(','),
        },
      },
    );
    return response.data;
  } catch (error) {
    // A deleted or deactivated shop answers an old link with 404 / 410.
    const status = readStatusCode(error);
    const log =
      isNoResponse(error) || status === 404 || status === 410
        ? Logger.warn
        : Logger.error;
    log('Error fetching shop detail', { error });

    throw error;
  }
}

export const shopDetailQuery = (
  shopId?: string | null,
  options?: UseShopDetailQueryOptions,
) => {
  const { include = ['address', 'staticMap', 'photos', 'waitingLists'] } =
    options || {};

  return queryOptions({
    queryKey: ['shop', shopId, include],
    async queryFn() {
      if (!shopId) {
        throw new Error('Shop ID is required');
      }

      return await getShopDetail(shopId, include);
    },
    enabled: !!shopId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: retryOnServerError,
    ...options,
  });
};
